// PRD 14 §3.4 — read-only metadata grid for a single version. Shared by the
// detail read view and the edit-draft screen so both render the same fields
// (Status / Version / Effective Date / Registered · Published Datetime /
// Revision Reason / Author · Publisher) with identical formatting.
//
// SHCT-709 — 개정 사유·시행일 are editable while the version is DRAFT, so the edit
// screen renders them as inputs and passes them in `omit` to keep this grid from
// showing the same value twice (once stale, once live). Every field the grid
// does render stays display-only.

import { useTranslation } from 'react-i18next';

import type { TermVersionSummary } from '../types/terms.types';
import { AuthorCell } from './AuthorCell';
import { TermStatusBadge } from './TermStatusBadge';

type OmittableField = 'effectiveDate' | 'revisionNote';

interface VersionMetaGridProps {
  version: TermVersionSummary;
  omit?: ReadonlyArray<OmittableField>;
}

function formatEffectiveDate(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return value.replace(/-/g, '.');
}

function formatRegisteredAt(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${mo}.${da} ${hh}:${mm}`;
}

const metaLabelStyle: React.CSSProperties = {
  // Match the prototype: labels are bold (`<strong>`) at body size (not the tiny
  // xs token); muted color keeps them distinct from the value next to them.
  color: 'var(--muted-foreground)',
  fontWeight: 'var(--font-weight-bold)',
  marginRight: 6,
};

const metaRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexWrap: 'wrap',
};

export function VersionMetaGrid({ version, omit = [] }: VersionMetaGridProps) {
  const { t } = useTranslation('terms');
  const revisionNoteText = version.revisionNote?.trim()
    ? version.revisionNote
    : t('list.noRevisionNote');
  const shows = (field: OmittableField) => !omit.includes(field);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
        padding: 12,
        background: 'var(--muted)',
        borderRadius: 6,
      }}
    >
      <div style={metaRowStyle}>
        <span style={metaLabelStyle}>{t('detail.fields.status')}</span>
        <TermStatusBadge status={version.status} />
      </div>
      <div style={metaRowStyle}>
        <span style={metaLabelStyle}>{t('detail.fields.version')}</span>
        <span style={{ fontWeight: 600, color: 'var(--info)' }}>{version.version}</span>
      </div>
      {shows('effectiveDate') ? (
        <div style={metaRowStyle}>
          <span style={metaLabelStyle}>{t('detail.fields.effectiveDate')}</span>
          <span>{formatEffectiveDate(version.effectiveDate)}</span>
        </div>
      ) : null}
      <div style={metaRowStyle}>
        <span style={metaLabelStyle}>{t('detail.fields.registeredAt')}</span>
        <span>{formatRegisteredAt(version.registeredAt)}</span>
      </div>
      {shows('revisionNote') ? (
        <div style={{ ...metaRowStyle, gridColumn: '1 / 3' }}>
          <span style={metaLabelStyle}>{t('detail.fields.revisionNote')}</span>
          <span>{revisionNoteText}</span>
        </div>
      ) : null}
      <div style={{ ...metaRowStyle, gridColumn: '1 / 3' }}>
        <span style={metaLabelStyle}>{t('detail.fields.author')}</span>
        <AuthorCell author={version.author} />
      </div>
    </div>
  );
}
